import { useState } from 'react'
import { Plus, Check, X, Edit2, Trash2, GripVertical } from 'lucide-react'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Textarea } from '@/components/ui/textarea'

interface InlineListEditProps {
  items: string[]
  onSave: (items: string[]) => Promise<boolean>
  placeholder?: string
  addButtonText?: string
  itemType?: 'ingredient' | 'instruction'
  disabled?: boolean
}

export function InlineListEdit({
  items,
  onSave,
  placeholder = 'Voeg item toe...',
  addButtonText = 'Item toevoegen',
  itemType = 'ingredient',
  disabled = false
}: InlineListEditProps) {
  const [isEditing, setIsEditing] = useState(false)
  const [editItems, setEditItems] = useState(items)
  const [isSaving, setIsSaving] = useState(false)

  const startEditing = () => {
    setEditItems([...items])
    setIsEditing(true)
  }

  const handleSave = async () => {
    // Filter out empty items
    const filteredItems = editItems.filter(item => item.trim().length > 0)
    
    setIsSaving(true)
    try {
      const success = await onSave(filteredItems)
      if (success) {
        setIsEditing(false)
      }
    } catch (error) {
      console.error('Error saving list:', error)
    } finally {
      setIsSaving(false)
    }
  }

  const handleCancel = () => {
    setEditItems([...items])
    setIsEditing(false)
  }

  const updateItem = (index: number, value: string) => {
    const newItems = [...editItems]
    newItems[index] = value
    setEditItems(newItems)
  }

  const addItem = () => {
    setEditItems([...editItems, ''])
  }

  const removeItem = (index: number) => {
    const newItems = editItems.filter((_, i) => i !== index)
    setEditItems(newItems)
  }

  const moveItem = (index: number, direction: 'up' | 'down') => {
    if (
      (direction === 'up' && index === 0) ||
      (direction === 'down' && index === editItems.length - 1)
    ) {
      return
    }

    const newItems = [...editItems]
    const targetIndex = direction === 'up' ? index - 1 : index + 1
    const temp = newItems[index]
    newItems[index] = newItems[targetIndex]
    newItems[targetIndex] = temp
    setEditItems(newItems)
  }

  const handleKeyDown = (e: React.KeyboardEvent, index: number) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault()
      if (index === editItems.length - 1) {
        // Add new item if we're on the last item
        addItem()
      } else {
        // Focus next item
        const nextInput = document.querySelector(
          `[data-item-index="${index + 1}"]`
        ) as HTMLElement
        nextInput?.focus()
      }
    } else if (e.key === 'Backspace' && editItems[index] === '') {
      e.preventDefault()
      if (editItems.length > 1) {
        removeItem(index)
        // Focus previous item if available
        if (index > 0) {
          setTimeout(() => {
            const prevInput = document.querySelector(
              `[data-item-index="${index - 1}"]`
            ) as HTMLElement
            prevInput?.focus()
          }, 0)
        }
      }
    }
  }

  if (disabled) {
    return (
      <div className="space-y-2">
        {items.map((item, index) => (
          <div key={index} className="flex items-start gap-3">
            <span className="flex-shrink-0 w-6 h-6 bg-orange-100 text-orange-600 rounded-full flex items-center justify-center text-sm font-medium mt-0.5">
              {index + 1}
            </span>
            <span className="flex-1 text-gray-900">{item}</span>
          </div>
        ))}
      </div>
    )
  }

  if (!isEditing) {
    return (
      <div className="group">
        <div className="space-y-2 mb-4">
          {items.map((item, index) => (
            <div key={index} className="flex items-start gap-3">
              <span className="flex-shrink-0 w-6 h-6 bg-orange-100 text-orange-600 rounded-full flex items-center justify-center text-sm font-medium mt-0.5">
                {index + 1}
              </span>
              <span className="flex-1 text-gray-900">{item}</span>
            </div>
          ))}
        </div>

        <Button
          variant="outline"
          onClick={startEditing}
          className="opacity-60 group-hover:opacity-100 transition-opacity"
        >
          <Edit2 className="h-4 w-4 mr-2" />
          Bewerk {itemType === 'ingredient' ? 'ingrediënten' : 'instructies'}
        </Button>
      </div>
    )
  }

  return (
    <div className="space-y-4">
      <div className="space-y-3">
        {editItems.map((item, index) => (
          <div key={index} className="flex items-start gap-2">
            <div className="flex flex-col gap-1 mt-1">
              <Button
                variant="ghost"
                size="sm"
                onClick={() => moveItem(index, 'up')}
                disabled={index === 0}
                className="h-6 w-6 p-0 opacity-50 hover:opacity-100"
              >
                <GripVertical className="h-3 w-3" />
              </Button>
            </div>

            <span className="flex-shrink-0 w-6 h-6 bg-orange-100 text-orange-600 rounded-full flex items-center justify-center text-sm font-medium mt-2">
              {index + 1}
            </span>

            {itemType === 'instruction' ? (
              <Textarea
                value={item}
                onChange={(e) => updateItem(index, e.target.value)}
                onKeyDown={(e) => handleKeyDown(e, index)}
                placeholder={placeholder}
                rows={2}
                className="flex-1 resize-none"
                data-item-index={index}
              />
            ) : (
              <Input
                value={item}
                onChange={(e) => updateItem(index, e.target.value)}
                onKeyDown={(e) => handleKeyDown(e, index)}
                placeholder={placeholder}
                className="flex-1"
                data-item-index={index}
              />
            )}

            <Button
              variant="ghost"
              size="sm"
              onClick={() => removeItem(index)}
              disabled={editItems.length === 1}
              className="h-8 w-8 p-0 text-red-600 hover:text-red-700 hover:bg-red-50 mt-1"
            >
              <Trash2 className="h-3 w-3" />
            </Button>
          </div>
        ))}
      </div>

      <div className="flex items-center gap-2">
        <Button
          variant="outline"
          size="sm"
          onClick={addItem}
          className="flex-1"
        >
          <Plus className="h-4 w-4 mr-2" />
          {addButtonText}
        </Button>
      </div>

      <div className="flex items-center gap-2 pt-4 border-t">
        <Button
          onClick={handleSave}
          disabled={isSaving}
          className="flex items-center gap-2"
        >
          {isSaving ? (
            <>
              <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
              Opslaan...
            </>
          ) : (
            <>
              <Check className="h-4 w-4" />
              Opslaan
            </>
          )}
        </Button>
        
        <Button
          variant="outline"
          onClick={handleCancel}
          disabled={isSaving}
        >
          <X className="h-4 w-4 mr-2" />
          Annuleren
        </Button>
      </div>
    </div>
  )
}